import React, { useState, useEffect } from 'react';
import ShareIcon from '@mui/icons-material/Share';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
// 1. Import the thinner chevron icon
import { 
  Dialog, 
  DialogContent, 
  DialogActions, 
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  CircularProgress,
  Box,
  TableSortLabel
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

// CHANGED: Import the real API instead of dummy data
import { dataApi } from '../services/dataApi';
import { api as researchApi } from '../services/api';

// --- SORTING HELPERS ---
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function ManageSharedLinks() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [links, setLinks] = useState([]);
  const [selected, setSelected] = useState([]);
  
  const [order, setOrder] = useState('desc'); 
  // CHANGED: Default sort by 'createdAt' (matching DB field name)
  const [orderBy, setOrderBy] = useState('createdAt'); 

  useEffect(() => {
    if (open) {
      setLoading(true);
      // CHANGED: Use real API call
      dataApi.fetchSharedLinks()
        .then(data => {
          setLinks(data || []); // Ensure data is an array
          setLoading(false);
        })
        .catch((err) => {
          console.error("Failed to fetch links", err);
          setLoading(false);
        });
    }
  }, [open]);

  const handleDelete = async (idsToDelete) => {
    try {
      setLoading(true);
      
      // Calls new secure API with list of IDs
      await researchApi.deleteSharedLinks(idsToDelete);
      
      // --- prune the just-deleted items from local state ----------
      setLinks(prev => prev.filter(link => !idsToDelete.includes(link.shareID)));
      setSelected([]);
      setLoading(false);
      
    } catch (error) {
      console.error("Failed to delete shared links", error);
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = links.map((n) => n.shareID);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp * 1000).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  // Common style for headers to ensure "darker" look when active
  const headerSortStyle = {
    fontWeight: 'bold',
    '&.Mui-active': { color: 'text.primary' }, // Standard dark color when active
    '&.Mui-active .MuiTableSortLabel-icon': { color: 'text.primary' }
  };

  return (
    <>
      <button 
        className="button"
        onClick={() => setOpen(true)}
        style={{ 
          padding: "6px 16px",
          fontSize: "0.875rem",
          minWidth: "64px",
          background: "linear-gradient(135deg, var(--tertiary-color), var(--secondary-color))",
          color: "white",
          fontWeight: "500",
          borderRadius: "4px",
          border: "none",
          boxShadow: "0 3px 5px rgba(0,0,0,0.2)",
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          cursor: 'pointer',
          transition: "all 0.3s ease",
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 6px 10px rgba(0,0,0,0.2)";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 3px 5px rgba(0,0,0,0.2)";
        }}
      >
        <ShareIcon style={{ fontSize: '1.2rem' }} />
        Manage Shared Links
      </button>

      <Dialog 
        open={open} 
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: { borderRadius: '12px', padding: '10px' }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '16px 24px 8px' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              Manage Shared Links
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total: {links.length}
            </Typography>
          </Box>
          
          {selected.length > 0 && (
            <Button 
              color="error" 
              startIcon={<DeleteOutlineIcon />}
              onClick={() => handleDelete(selected)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Delete ({selected.length})
            </Button>
          )}
        </Box>

        <DialogContent sx={{ padding: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <CircularProgress sx={{ color: 'var(--tertiary-color)' }} />
            </Box>
          ) : links.length === 0 ? (
            // EMPTY STATE
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              padding: '60px 20px',
              gap: 1 
            }}>
              <ShareIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="textSecondary" sx={{ fontWeight: 500 }}>
                No shared links found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                When you share your research, the active links will appear here.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selected.length > 0 && selected.length < links.length}
                        checked={links.length > 0 && selected.length === links.length}
                        onChange={handleSelectAllClick}
                        sx={{
                          color: 'var(--tertiary-color)',
                          '&.Mui-checked': { color: 'var(--tertiary-color)' },
                          '&.MuiCheckbox-indeterminate': { color: 'var(--tertiary-color)' }
                        }}
                      />
                    </TableCell>
                    
                    {/* Title */}
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'title'}
                        direction={orderBy === 'title' ? order : 'asc'}
                        onClick={() => handleRequestSort('title')}
                        sx={headerSortStyle}
                      >
                        Research Title
                        {orderBy === 'title' ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>

                    {/* NEW: Created Date */}
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'createdAt'}
                        direction={orderBy === 'createdAt' ? order : 'asc'}
                        onClick={() => handleRequestSort('createdAt')}
                        sx={headerSortStyle}
                      >
                        Created At
                        {orderBy === 'createdAt' ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>

                    {/* Expires At */}
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'expiresAt'}
                        direction={orderBy === 'expiresAt' ? order : 'asc'}
                        onClick={() => handleRequestSort('expiresAt')}
                        sx={headerSortStyle}
                      >
                        Expires At
                        {orderBy === 'expiresAt' ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stableSort(links, getComparator(order, orderBy)).map((row) => {
                    const isItemSelected = isSelected(row.shareID);
                    
                    // --- URL CONSTRUCTION ---
                    const baseUrl = window.location.origin;
                    
                    const fullUrl = `${baseUrl}/shared/${row.shareID}`;
                    // ------------------------

                    return (
                      <TableRow
                        hover
                        onClick={(event) => handleClick(event, row.shareID)}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row.shareID}
                        selected={isItemSelected}
                        sx={{ 
                          cursor: 'pointer',
                          '&.Mui-selected': { backgroundColor: 'rgba(var(--tertiary-rgb), 0.08)' },
                          '&.Mui-selected:hover': { backgroundColor: 'rgba(var(--tertiary-rgb), 0.12)' }
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            sx={{
                              color: 'var(--tertiary-color)',
                              '&.Mui-checked': { color: 'var(--tertiary-color)' }
                            }}
                          />
                        </TableCell>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                          {row.title}
                        </TableCell>
                        {/* New Created Date Cell */}
                        <TableCell color="textSecondary">
                          {formatDate(row.createdAt)}
                        </TableCell>
                        <TableCell color="textSecondary">
                          {formatDate(row.expiresAt)}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button
                              size="small"
                              endIcon={<OpenInNewIcon fontSize="small" />}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(fullUrl, '_blank');
                              }}
                              sx={{ 
                                textTransform: 'none', 
                                color: 'var(--tertiary-color)',
                                fontWeight: 600
                              }}
                            >
                              Open
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete([row.shareID]);
                              }}
                              sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={() => setOpen(false)} 
            variant="contained"
            sx={{
              backgroundColor: 'var(--tertiary-color)',
              textTransform: 'none',
              '&:hover': { backgroundColor: 'var(--dark-green)' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ManageSharedLinks;